import { useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { isEmpty, isNil } from 'lodash';
// Import Store
import { useAppSelector } from 'store';
import { selectPartnerSettingsSubscription } from 'store/slices/partnerSettings';
// Import UI Components
import { Icon, Tooltip } from '@geneui/components';
// Import Components
import FilterDependencyDots, { DependencyPill, FilterInfoPopover } from 'components/common/FilterDependencyDots';
// Import Configs
import { comparisonIconsTypes } from './config';
// Import Constants
import { hashTypeMap } from './constants';
// Import Enums
import { SegmentationCategoryTypeEnum } from 'enums';
// Import Styles
import styles from './DynamicSegmentListItem.module.scss';

const DynamicSegmentListItem = (props) => {
    const {
        text,
        color,
        uiType,
        handleClick,
        selected,
        comparisonIconType,
        type,
        description,
        segmentationFilterId,
        schemaDeps,
    } = props;
    const { Columns } = useAppSelector(selectPartnerSettingsSubscription) ?? {};
    const ComparisonIcon = comparisonIconsTypes[comparisonIconType] || false;
    const [isTextTruncated, setIsTextTruncated] = useState(false);

    const textRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const el = textRef.current;
        if (el) {
            setIsTextTruncated(el.scrollWidth > el.clientWidth);
        }
    }, [text]);

    const iconType = useMemo(() => {
        if (uiType === SegmentationCategoryTypeEnum.None) {
            return selected ? 'bc-icon-folder-opened' : 'bc-icon-folder-closed';
        }

        if (uiType === SegmentationCategoryTypeEnum.AIPredicted) {
            return selected ? 'bc-icon-folder-AI-opened' : 'bc-icon-folder-AI-closed';
        }
    }, [selected, uiType]);

    const isDisabled = useMemo(() => {
        return !Columns?.includes(segmentationFilterId) && !isNil(segmentationFilterId);
    }, [Columns, segmentationFilterId]);

    const hasDependencies = schemaDeps.length > 0;
    const hasDescription = !isEmpty(description);

    return (
        <li
            className={`crm-flex crm-align-items-center crm-dynamic-segment-list-item
            ${selected ? 'crm-active' : ''} ${isDisabled ? 'disabled' : ''}`}
            onClick={!isDisabled ? handleClick : undefined}
            role="presentation"
        >
            {ComparisonIcon ? (
                <Icon
                    className={classNames('crm-dynamic-segment-list-item-icon', 'comparison-icon', {
                        'ai-predicted': uiType === SegmentationCategoryTypeEnum.AIPredicted,
                    })}
                    type={ComparisonIcon}
                    style={{ backgroundColor: color }}
                />
            ) : type !== hashTypeMap.category ? (
                <span
                    className={classNames('crm-dynamic-segment-list-item-color-circle', {
                        'ai-predicted': uiType === SegmentationCategoryTypeEnum.AIPredicted,
                    })}
                >
                    <span style={{ backgroundColor: color }} />
                </span>
            ) : (
                // todo it's a really bad idea!
                <Icon
                    className={classNames('crm-dynamic-segment-list-item-icon', 'folder', {
                        'ai-predicted': uiType === SegmentationCategoryTypeEnum.AIPredicted,
                    })}
                    type={iconType}
                    style={{ '--dynamic-segment-list-item-icon': color }}
                />
            )}
            <Tooltip text={isTextTruncated ? text : ''} position="bottom">
                <h6 ref={textRef} className={styles.listItemText}>
                    {text}
                </h6>
            </Tooltip>
            {(hasDependencies || hasDescription) && (
                <DependencyPill className={styles.listItemDependencyPill}>
                    {hasDependencies && <FilterDependencyDots schemaDeps={schemaDeps} />}
                    {hasDescription && <FilterInfoPopover title={text} description={description} />}
                </DependencyPill>
            )}
        </li>
    );
};

DynamicSegmentListItem.propTypes = {
    text: PropTypes.string,
    description: PropTypes.string,
    color: PropTypes.string,
    uiType: PropTypes.number,
    handleClick: PropTypes.func,
    resetActiveList: PropTypes.func,
    full: PropTypes.bool,
    selected: PropTypes.bool,
    comparisonIconType: PropTypes.number,
    type: PropTypes.string,
    segmentationFilterId: PropTypes.string,
    schemaDeps: PropTypes.array,
};
DynamicSegmentListItem.defaultProps = {
    full: true,
    comparisonIconType: -1,
    description: '',
    schemaDeps: [],
};

export default DynamicSegmentListItem;
